import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { Subscription, fromEvent, merge, timer } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth.service';
import { ConfirmationDialogService } from '../modal/service/confirm-modal.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-idle-timeout',
  template: ``,
  styles: []
})
export class IdleTimeoutComponent implements OnInit, OnDestroy {
  private readonly idleTime = 15 * 60; // 15 minutes in seconds
  private readonly warningTime = 5 * 60; // Show warning 5 minutes before logout (in seconds)
  private idleTimer: Subscription;
  private warningTimer: Subscription;
  private activitySubscription: Subscription;
  
  private modalOpened = false;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly dialogSvc: ConfirmationDialogService
  ) {}

  ngOnInit() {
    console.log('IdleTimeoutComponent initialized');
    // Only start monitoring if user is logged in
    this.authService.isUserLoggedInEventEmitter.subscribe(isLoggedIn => {
      console.log('User logged in status changed:', isLoggedIn);
      if (isLoggedIn) {
        this.startMonitoring();
      } else {
        this.stopMonitoring();
      }
    });

    // Start monitoring if already logged in
    const isLoggedIn = this.authService.isUserLoggedIn();
    console.log('Current login status:', isLoggedIn);
    if (isLoggedIn) {
      this.startMonitoring();
    }
  }

  ngOnDestroy() {
    this.stopMonitoring();
  }

  private startMonitoring() {
    console.log('Starting idle timeout monitoring...');
    // Stop any existing monitoring
    this.stopMonitoring();

    // Listen to user activity events
    // Throttle to maximum once per second to avoid excessive timer resets
    this.ngZone.runOutsideAngular(() => {
      const events = [
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'mousedown'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'scroll'),
        fromEvent(document, 'touchstart'),
        fromEvent(document, 'click')
      ];

      this.activitySubscription = merge(...events)
        .pipe(throttleTime(1000)) // Only trigger once per second maximum
        .subscribe(() => {
          this.resetTimer();
        });
    });

    // Start the idle timer
    this.resetTimer();
  }

  private stopMonitoring() {
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
    }
    if (this.idleTimer) {
      this.idleTimer.unsubscribe();
    }
    if (this.warningTimer) {
      this.warningTimer.unsubscribe();
    }
    this.modalOpened = false;
  }

  private resetTimer() {
    // Don't reset if warning modal is being shown
    if (this.modalOpened) {
      return;
    }
    
    // Clear existing timers
    if (this.idleTimer) {
      this.idleTimer.unsubscribe();
    }
    if (this.warningTimer) {
      this.warningTimer.unsubscribe();
    }

    // Set timer to show warning before idle timeout
    const timeUntilWarning = (this.idleTime - this.warningTime) * 1000;
    this.warningTimer = timer(timeUntilWarning).subscribe(() => {
      this.showWarningDialog();
    });

    // Set timer for full idle timeout (logout)
    this.idleTimer = timer(this.idleTime * 1000).subscribe(() => {
      this.logout();
    });
  }

  private showWarningDialog() {
    console.log('Showing warning dialog!');
    this.ngZone.run(() => {
      this.modalOpened = true;
      
      const minutes = Math.floor(this.warningTime / 60);
      const message = `Your session will expire in ${minutes} minute${minutes > 1 ? 's' : ''} due to inactivity.\n\nTo keep working, select Stay signed in`;
      
      // Show the confirmation dialog with dismiss disabled
      this.dialogSvc.confirm('Session Expiring Soon', message, true, false, 'Stay signed in', 'Sign out', true)
        .then((result) => {
          if (result) {
            // User clicked "Confirm" to continue session
            console.log('User chose to continue the session.');
            this.continueSession();
          } else {
            // User clicked "Cancel" to logout
            this.logout();
          }
        })
        .catch(() => {
          // Dialog dismissed (e.g., ESC key or backdrop click without clicking a button)
          // Treat as logout
          this.logout();
        });
    });
  }

  public continueSession() {
    this.modalOpened = false;
    this.resetTimer();
  }

  public logout() {
    this.stopMonitoring();
    this.modalOpened = false;
    
    // Perform logout
    this.authService.logoutUser(true);
  }
}
